import {
  LEAVE_CALENDAR_AWAY_FIELDS,
  type LeaveAbsenceType,
  type LeaveCalendarDay,
  type LeaveCalendarEntry,
} from '@gb-ticket/shared-types';

import { cn } from '@/lib/utils';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { LeaveDayCell } from '@/products/leave/features/leave-day-cell';
import {
  buildCalendarCells,
  formatDayLabel,
  formatGermanDate,
  WEEKDAYS,
} from '@/products/leave/features/leave-calendar-utils';

type Props = {
  year: number;
  month: number;
  days: readonly LeaveCalendarDay[];
  today?: string;
  rangeStart?: string | null;
  onPickDate?: (date: string) => void;
  className?: string;
};

export function LeaveCalendarMonth({
  year,
  month,
  days,
  today,
  rangeStart = null,
  onPickDate,
  className,
}: Props) {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const cells = buildCalendarCells(year, month);

  return (
    <div className={cn(leavePanelClassName, className ?? 'hidden lg:block')}>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
        {WEEKDAYS.map((label) => (
          <div key={label} className="py-2">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, index) => {
          if (!cell.date || cell.day == null) {
            return (
              <div key={`empty-${index}`} className="min-h-24 rounded-md bg-muted/20" aria-hidden />
            );
          }
          const date = cell.date;
          const day = byDate.get(date);
          const holiday = day?.holidays[0];
          const isToday = today === date;
          return (
            <div
              key={date}
              tabIndex={onPickDate ? undefined : 0}
              aria-current={isToday ? 'date' : undefined}
              className={cn(
                'min-h-24 rounded-md border bg-card p-2 text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                isToday ? 'border-primary ring-1 ring-primary' : null,
                rangeStart === date ? 'ring-2 ring-primary' : null,
              )}
              aria-label={formatDayLabel(date, holiday?.nameDe)}
            >
              <div className="flex items-start justify-between gap-1">
                {onPickDate ? (
                  <button
                    type="button"
                    className="text-sm font-semibold hover:underline"
                    aria-pressed={rangeStart === date}
                    onClick={() => onPickDate(date)}
                  >
                    {cell.day}
                  </button>
                ) : (
                  <span className="text-sm font-semibold">{cell.day}</span>
                )}
                {holiday ? (
                  <span
                    className="text-[10px] font-medium text-muted-foreground"
                    title={holiday.nameDe}
                  >
                    FT
                  </span>
                ) : null}
              </div>
              {holiday ? (
                <p className="mt-1 text-[10px] leading-tight text-muted-foreground">
                  {holiday.nameDe}
                </p>
              ) : null}
              <ul className="mt-1 space-y-0.5">
                {day?.entries.map((entry) => (
                  <EntryRow key={`${entry.requestId}-${entry.part}`} entry={entry} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LeaveCalendarDayList({
  days,
  today,
  rangeStart = null,
  onPickDate,
  className,
}: {
  days: readonly LeaveCalendarDay[];
  today?: string;
  rangeStart?: string | null;
  onPickDate?: (date: string) => void;
  className?: string;
}) {
  const withEntries = days.filter((day) => day.entries.length > 0 || day.holidays.length > 0);
  if (withEntries.length === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>Niemand abwesend</p>;
  }
  return (
    <ul className={cn('space-y-4', className)}>
      {withEntries.map((day) => (
        <li
          key={day.date}
          className={cn(
            leavePanelClassName,
            today === day.date ? 'border-primary ring-1 ring-primary' : null,
          )}
        >
          {onPickDate ? (
            <button
              type="button"
              className="font-medium hover:underline"
              aria-pressed={rangeStart === day.date}
              onClick={() => onPickDate(day.date)}
            >
              {formatGermanDate(day.date)}
            </button>
          ) : (
            <p className="font-medium">{formatGermanDate(day.date)}</p>
          )}
          {day.holidays.map((holiday) => (
            <p key={holiday.nameDe} className="text-sm text-muted-foreground">
              Feiertag: {holiday.nameDe}
            </p>
          ))}
          <ul className="mt-2 space-y-1">
            {day.entries.map((entry) => (
              <EntryRow key={`${entry.requestId}-${entry.part}`} entry={entry} dense={false} />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function EntryRow({ entry, dense = true }: { entry: LeaveCalendarEntry; dense?: boolean }) {
  const status =
    entry.status === 'approved' || entry.status === 'submitted' ? entry.status : 'approved';

  return (
    <li className={cn('flex items-start gap-1.5', dense ? 'text-[11px]' : 'text-sm')}>
      <LeaveDayCell
        size="chip"
        color={entry.color}
        mark={entry.mark}
        status={status}
        part={entry.part}
        labelDe={entry.labelDe}
      />
      <span className="min-w-0 leading-tight">
        <span className="font-medium">{entry.displayName}</span>
        <span className="text-muted-foreground">
          {' '}
          · {entry.labelDe}
          {status === 'submitted' ? ' · Eingereicht' : ''}
        </span>
        {entry.substituteName ? (
          <span className={cn('text-muted-foreground', dense ? 'block' : null)}>
            {dense ? 'V: ' : ' · Vertretung: '}
            {entry.substituteName}
          </span>
        ) : null}
      </span>
    </li>
  );
}

export function LeaveCalendarLegend({ types }: { types: readonly LeaveAbsenceType[] }) {
  const sample = types[0] ?? null;
  return (
    <ul
      className={cn(
        leavePanelClassName,
        'flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground',
      )}
    >
      {types.map((type) => (
        <li key={type.id} className="flex items-center gap-1.5">
          <LeaveDayCell
            size="chip"
            color={type.color}
            mark={type.mark}
            status="approved"
            labelDe={type.labelDe}
          />
          <span className="font-medium text-foreground">{type.labelDe}</span>
        </li>
      ))}
      <li className="flex items-center gap-1.5">
        <span className="inline-flex size-7 rounded-sm border bg-muted" aria-hidden />
        <span className="font-medium text-foreground">Wochenende</span>
      </li>
      <li className="flex items-center gap-1.5">
        <span className="inline-flex size-7 rounded-sm border border-red-500 bg-card" aria-hidden />
        <span className="font-medium text-foreground">Feiertag</span>
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="inline-flex size-7 rounded-sm border bg-orange-100 dark:bg-orange-950/50"
          aria-hidden
        />
        <span className="font-medium text-foreground">Schulferien</span>
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="inline-flex size-7 rounded-sm border bg-card ring-1 ring-primary"
          aria-hidden
        />
        <span className="font-medium text-foreground">Heute</span>
      </li>
      {sample ? (
        <>
          <li className="flex items-center gap-1.5">
            <LeaveDayCell
              size="chip"
              color={sample.color}
              mark={sample.mark}
              status="approved"
              labelDe={sample.labelDe}
            />
            <span className="font-medium text-foreground">Genehmigt</span>
          </li>
          <li className="flex items-center gap-1.5">
            <LeaveDayCell
              size="chip"
              color={sample.color}
              mark={sample.mark}
              status="submitted"
              labelDe={sample.labelDe}
            />
            <span className="font-medium text-foreground">Eingereicht</span>
          </li>
        </>
      ) : null}
      <li className="flex items-center gap-1.5">
        <LeaveDayCell
          size="chip"
          color={LEAVE_CALENDAR_AWAY_FIELDS.color}
          mark={LEAVE_CALENDAR_AWAY_FIELDS.mark}
          status="approved"
          labelDe={LEAVE_CALENDAR_AWAY_FIELDS.labelDe}
        />
        <span className="font-medium text-foreground">{LEAVE_CALENDAR_AWAY_FIELDS.labelDe}</span>
      </li>
    </ul>
  );
}
